import { foldAccents } from "./text";

/*
  The A-Z section's grouping (§4.4).

  Trim, accent-fold, uppercase the first character; anything that is not /^[A-Z]$/
  groups under "#". Games are deduped by id and sorted within their group by
  localeCompare. Admin's AlphabeticalGrid.tsx:151-166 dedupes by name; this no longer
  does, because two catalog rows can share a name (see findDuplicateNames).
*/

export const OTHER_GROUP_KEY = "#";

export interface NamedGame {
  id: number;
  name: string;
}

export interface AlphabeticalGroup<T extends NamedGame> {
  key: string;
  games: T[];
}

export const groupKeyForName = (name: string): string => {
  const firstChar = foldAccents(name.trim()).charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstChar) ? firstChar : OTHER_GROUP_KEY;
};

/*
  "#" sorts LAST. A plain localeCompare puts it first, and production has a "#" game
  today (the Japanese-titled Love Live game), so this is visible on day one rather than
  latent.
*/
export const compareGroupKeys = (a: string, b: string): number => {
  if (a === b) {
    return 0;
  }
  if (a === OTHER_GROUP_KEY) {
    return 1;
  }
  if (b === OTHER_GROUP_KEY) {
    return -1;
  }
  return a.localeCompare(b, "en");
};

export const compareGameNames = <T extends NamedGame>(a: T, b: T): number =>
  a.name.localeCompare(b.name, "en");

/*
  Deduped on the id, first occurrence winning. Keying on the name hid one of two
  same-named games (Battle Spirits Saga is ids 14 and 27) and its gradings with it.
*/
const dedupeById = <T extends NamedGame>(games: readonly T[]): T[] => {
  const seen = new Map<number, T>();
  games.forEach((game) => {
    if (!seen.has(game.id)) {
      seen.set(game.id, game);
    }
  });
  return Array.from(seen.values());
};

/* Trimmed and lower-cased: the key two names count as "the same" under. */
export const nameKey = (name: string): string => name.trim().toLowerCase();

/*
  The name keys more than one distinct game carries. Those games are labelled with their
  game code as well, or the page would show two identical links.
*/
export const findDuplicateNames = <T extends NamedGame>(
  games: readonly T[]
): ReadonlySet<string> => {
  const counts = new Map<string, number>();
  dedupeById(games).forEach((game) => {
    const key = nameKey(game.name);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return new Set(
    Array.from(counts.keys()).filter((key) => (counts.get(key) as number) > 1)
  );
};

export const groupGamesAlphabetically = <T extends NamedGame>(
  games: readonly T[]
): AlphabeticalGroup<T>[] => {
  const groups = new Map<string, T[]>();

  dedupeById(games).forEach((game) => {
    const key = groupKeyForName(game.name);
    const existing = groups.get(key);
    if (existing) {
      existing.push(game);
    } else {
      groups.set(key, [game]);
    }
  });

  return Array.from(groups.keys())
    .sort(compareGroupKeys)
    .map((key) => ({
      key,
      games: (groups.get(key) as T[]).slice().sort(compareGameNames),
    }));
};
